import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../lib/api";

const emptyAddress = {
  label: "Otthon",
  country: "HU",
  postalCode: "",
  city: "",
  street: "",
  houseNumber: "",
  floorDoor: "",
  recipientName: "",
  phone: "",
};

export default function AccountPage() {
  const { user, logout, deleteAccount, applyUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profil");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    displayName: "",
  });
  const [company, setCompany] = useState({
    isCompany: false,
    name: "",
    taxNumber: "",
    vatNumber: "",
    companyRegistryNumber: "",
  });
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    newPassword2: "",
  });
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [deletePassword, setDeletePassword] = useState("");

  useEffect(() => {
    if (!user) return;
    setProfile({
      firstName: user.profile?.firstName || "",
      lastName: user.profile?.lastName || "",
      phone: user.profile?.phone || "",
      displayName: user.profile?.displayName || "",
    });
    setCompany({
      isCompany: !!user.company?.isCompany,
      name: user.company?.name || "",
      taxNumber: user.company?.taxNumber || "",
      vatNumber: user.company?.vatNumber || "",
      companyRegistryNumber: user.company?.companyRegistryNumber || "",
    });
  }, [user]);

  const flash = (ok, text) => {
    setErr(ok ? "" : text);
    setMsg(ok ? text : "");
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (company.isCompany) {
      if (!String(company.name || "").trim()) {
        flash(false, "Céges számlázásnál a cégnév kötelező.");
        return;
      }
      if (!String(company.taxNumber || "").trim()) {
        flash(false, "Céges számlázásnál az adószám kötelező.");
        return;
      }
    }
    setBusy(true);
    try {
      const data = await authApi.updateProfile({ profile, company });
      applyUser(data.user);
      flash(true, "Profil mentve.");
    } catch (ex) {
      flash(false, ex.message);
    } finally {
      setBusy(false);
    }
  };

  const resendVerify = async () => {
    setBusy(true);
    try {
      const data = await authApi.resendVerification();
      flash(true, data.message || "Megerősítő e-mail elküldve.");
    } catch (ex) {
      flash(false, ex.message);
    } finally {
      setBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.newPassword2) {
      flash(false, "A két új jelszó nem egyezik.");
      return;
    }
    setBusy(true);
    try {
      const data = await authApi.changePassword(
        passwords.currentPassword,
        passwords.newPassword
      );
      setPasswords({ currentPassword: "", newPassword: "", newPassword2: "" });
      flash(true, data.message || "Jelszó módosítva.");
    } catch (ex) {
      flash(false, ex.message);
    } finally {
      setBusy(false);
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await authApi.addAddress({
        ...addressForm,
        isDefaultShipping: false,
        isDefaultBilling: false,
      });
      applyUser(data.user);
      setAddressForm(emptyAddress);
      flash(true, "Cím hozzáadva.");
    } catch (ex) {
      flash(false, ex.message);
    } finally {
      setBusy(false);
    }
  };

  const removeAddress = async (id) => {
    setBusy(true);
    try {
      const data = await authApi.deleteAddress(id);
      applyUser(data.user);
      flash(true, "Cím törölve.");
    } catch (ex) {
      flash(false, ex.message);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      flash(false, "Add meg a jelszavad a fiók törléséhez.");
      return;
    }
    const sure = window.confirm(
      "Biztosan törölni szeretnéd a fiókodat? Ez a művelet nem vonható vissza."
    );
    if (!sure) return;

    setBusy(true);
    try {
      await deleteAccount(deletePassword);
      navigate("/", { replace: true });
    } catch (ex) {
      flash(false, ex.message);
      setBusy(false);
    }
  };

  if (!user) return null;

  const tabs = [
    { id: "profil", label: "Profil" },
    { id: "cim", label: "Címek" },
    { id: "jelszo", label: "Jelszó" },
    { id: "torles", label: "Fiók törlése" },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 pt-24 pb-12">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Fiókom</h1>
          <p className="text-gray-600 mt-1">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={() => logout()}
          className="border rounded-lg px-4 py-2 text-sm hover:bg-gray-50 shrink-0"
        >
          Kijelentkezés
        </button>
      </div>

      {!user.emailVerifiedAt && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 text-sm flex flex-wrap items-center justify-between gap-3">
          <span>Az e-mail címed még nincs megerősítve.</span>
          <button
            type="button"
            disabled={busy}
            onClick={resendVerify}
            className="underline font-medium"
          >
            Megerősítő e-mail újraküldése
          </button>
        </div>
      )}

      {(msg || err) && (
        <div
          className={`mb-6 rounded-lg px-3 py-2 text-sm border ${
            err
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          {err || msg}
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setMsg("");
              setErr("");
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#0c1a26] text-[#0c1a26]"
                : "border-transparent text-gray-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profil" && (
        <form onSubmit={saveProfile} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium">Keresztnév</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, firstName: e.target.value }))
                }
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Vezetéknév</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, lastName: e.target.value }))
                }
              />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium">Megjelenített név</span>
            <input
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={profile.displayName}
              onChange={(e) =>
                setProfile((p) => ({ ...p, displayName: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Telefon</span>
            <input
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={profile.phone}
              onChange={(e) =>
                setProfile((p) => ({ ...p, phone: e.target.value }))
              }
            />
          </label>

          <label className="flex items-center gap-2 text-sm pt-2">
            <input
              type="checkbox"
              checked={company.isCompany}
              onChange={(e) =>
                setCompany((c) => ({ ...c, isCompany: e.target.checked }))
              }
            />
            Céges számlázás
          </label>

          {company.isCompany && (
            <div className="space-y-3 rounded-xl border p-4 bg-gray-50">
              <label className="block">
                <span className="text-sm font-medium">
                  Cégnév <span className="text-red-600">*</span>
                </span>
                <input
                  required
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={company.name}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, name: e.target.value }))
                  }
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">
                  Adószám <span className="text-red-600">*</span>
                </span>
                <input
                  required
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={company.taxNumber}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, taxNumber: e.target.value }))
                  }
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">
                  EU adószám (VAT){" "}
                  <span className="text-gray-500 font-normal">(opcionális)</span>
                </span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={company.vatNumber}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, vatNumber: e.target.value }))
                  }
                  placeholder="pl. HU12345678"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">
                  Cégjegyzékszám{" "}
                  <span className="text-gray-500 font-normal">(opcionális)</span>
                </span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={company.companyRegistryNumber}
                  onChange={(e) =>
                    setCompany((c) => ({
                      ...c,
                      companyRegistryNumber: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="bg-[#0c1a26] text-[#f5e9cb] rounded-lg px-5 py-2.5 font-medium disabled:opacity-60"
          >
            Mentés
          </button>
        </form>
      )}

      {tab === "cim" && (
        <div className="space-y-8">
          <ul className="space-y-3">
            {(user.addresses || []).length === 0 && (
              <li className="text-gray-600 text-sm">Még nincs mentett cím.</li>
            )}
            {(user.addresses || []).map((a) => (
              <li
                key={a.id}
                className="border rounded-xl p-4 flex flex-wrap justify-between gap-3"
              >
                <div>
                  <div className="font-medium">{a.label}</div>
                  <div className="text-sm text-gray-700">
                    {a.recipientName}
                    <br />
                    {a.postalCode} {a.city}, {a.street} {a.houseNumber}
                    {a.floorDoor ? `, ${a.floorDoor}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-red-700 underline text-sm self-start"
                  onClick={() => removeAddress(a.id)}
                >
                  Törlés
                </button>
              </li>
            ))}
          </ul>

          <form onSubmit={addAddress} className="space-y-3 border-t pt-6">
            <h2 className="text-lg font-semibold">Új cím</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["label", "Címke", false],
                ["recipientName", "Címzett", true],
                ["postalCode", "Irányítószám", true],
                ["city", "Város", true],
                ["street", "Utca", true],
                ["houseNumber", "Házszám", true],
                ["floorDoor", "Emelet / ajtó", false],
                ["phone", "Telefon", false],
              ].map(([key, label, required]) => (
                <label key={key} className="block">
                  <span className="text-sm font-medium">
                    {label}
                    {!required && (
                      <span className="text-gray-500 font-normal">
                        {" "}
                        (opcionális)
                      </span>
                    )}
                  </span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={addressForm[key]}
                    onChange={(e) =>
                      setAddressForm((f) => ({ ...f, [key]: e.target.value }))
                    }
                    required={required}
                  />
                </label>
              ))}
            </div>
            <button
              type="submit"
              disabled={busy}
              className="bg-[#0c1a26] text-[#f5e9cb] rounded-lg px-5 py-2.5 font-medium disabled:opacity-60"
            >
              Cím hozzáadása
            </button>
          </form>
        </div>
      )}

      {tab === "jelszo" && (
        <form onSubmit={changePw} className="space-y-4 max-w-md">
          <label className="block">
            <span className="text-sm font-medium">Jelenlegi jelszó</span>
            <input
              type="password"
              required
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={passwords.currentPassword}
              onChange={(e) =>
                setPasswords((p) => ({
                  ...p,
                  currentPassword: e.target.value,
                }))
              }
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Új jelszó</span>
            <input
              type="password"
              required
              minLength={8}
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={passwords.newPassword}
              onChange={(e) =>
                setPasswords((p) => ({ ...p, newPassword: e.target.value }))
              }
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Új jelszó újra</span>
            <input
              type="password"
              required
              minLength={8}
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={passwords.newPassword2}
              onChange={(e) =>
                setPasswords((p) => ({ ...p, newPassword2: e.target.value }))
              }
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-[#0c1a26] text-[#f5e9cb] rounded-lg px-5 py-2.5 font-medium disabled:opacity-60"
          >
            Jelszó mentése
          </button>
        </form>
      )}

      {tab === "torles" && (
        <form
          onSubmit={onDeleteAccount}
          className="space-y-4 max-w-md rounded-xl border border-red-200 bg-red-50/60 p-5"
        >
          <h2 className="text-lg font-semibold text-red-900">Fiók végleges törlése</h2>
          <p className="text-sm text-red-900/80">
            A törlés után a profil-, cím- és bejelentkezési adataid eltávolításra
            kerülnek, és ki leszel jelentkeztetve. Ugyanazzal az e-mail címmel később
            újra regisztrálhatsz.
          </p>
          <label className="block">
            <span className="text-sm font-medium">Jelszó megerősítése</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              className="mt-1 w-full border border-red-200 rounded-lg px-3 py-2 bg-white"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-red-700 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-red-800 disabled:opacity-60"
          >
            {busy ? "Törlés…" : "Fiók törlése"}
          </button>
        </form>
      )}
    </div>
  );
}
